import { useLayoutEffect, useRef } from "react";

import {
  Paper,
  Portal,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton
} from "@mantine/core";

import type { MentionOption, MentionPopupPosition } from "./types";

import classes from "./styles.module.css";

type MentionListPortalProps = {
  id: string;
  options: MentionOption[];
  position: MentionPopupPosition | null;
  highlightedIndex: number;
  maxHeight: number;
  onHighlight: (index: number) => void;
  onHeightChange: (height: number) => void;
  onSelect: (mention: MentionOption) => void;
};

const MentionListPortal = (props: MentionListPortalProps) => {
  const {
    id,
    options,
    position,
    highlightedIndex,
    maxHeight,
    onHighlight,
    onHeightChange,
    onSelect
  } = props;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isOpen = position !== null;

  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) {
      return;
    }

    onHeightChange(dropdownRef.current.getBoundingClientRect().height);
  }, [isOpen, maxHeight, onHeightChange, options]);

  if (!position) {
    return null;
  }

  return (
    <Portal>
      <Paper
        ref={dropdownRef}
        id={id}
        role="listbox"
        className={classes.dropdown}
        style={{ top: position.top, left: position.left }}
        shadow="md"
        radius="md"
        withBorder
        p={4}
      >
        <ScrollArea.Autosize mah={maxHeight} type="scroll">
          {options.length ? (
            <Stack gap={2}>
              {options.map((option, index) => (
                <UnstyledButton
                  id={`${id}-option-${index}`}
                  key={option.id}
                  type="button"
                  role="option"
                  className={classes.option}
                  px={10}
                  py={8}
                  aria-selected={highlightedIndex === index}
                  data-active={highlightedIndex === index || undefined}
                  disabled={option.disabled}
                  onMouseEnter={() => {
                    if (!option.disabled) {
                      onHighlight(index);
                    }
                  }}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => onSelect(option)}
                >
                  <Text size="sm" truncate>
                    {option.label}
                  </Text>
                </UnstyledButton>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" size="sm" ta="center" px="sm" py="xs">
              No mentions found
            </Text>
          )}
        </ScrollArea.Autosize>
      </Paper>
    </Portal>
  );
};

export default MentionListPortal;
